import { CommunityEvent, QuoteRequest, QuoteResponse, ApiQuoteRequest, ApiQuoteResponse } from "@/types/rider";
import { createApiRequest } from "@/utils/apiUtils";
import { API_URL } from "@/config/api";
import { format } from "date-fns";

// Mock data for community events
const mockEvents: CommunityEvent[] = [
  {
    id: "1",
    name: "Coachella",
    location: "Indio, CA",
    region: "Los Angeles",
    image: "/lovable-uploads/425aa8d4-e767-4b41-82ee-0773c282877e.png"
  },
  {
    id: "2",
    name: "EDC",
    location: "Las Vegas, NV",
    region: "Los Angeles",
    image: "/lovable-uploads/425aa8d4-e767-4b41-82ee-0773c282877e.png"
  },
  {
    id: "3",
    name: "Beyond Wonderland",
    location: "San Bernardino, CA",
    region: "Los Angeles",
    image: "/lovable-uploads/425aa8d4-e767-4b41-82ee-0773c282877e.png"
  },
  {
    id: "4",
    name: "Los Angeles Lakers Playoffs Rd 1",
    location: "Crypto.com Arena, LA",
    region: "Los Angeles",
    image: "/lovable-uploads/425aa8d4-e767-4b41-82ee-0773c282877e.png"
  },
  {
    id: "5",
    name: "Rufus Du Sol",
    location: "Hollywood Bowl, LA",
    region: "Los Angeles",
    image: "/lovable-uploads/425aa8d4-e767-4b41-82ee-0773c282877e.png"
  }
];

export const getCommunityEvents = async (): Promise<CommunityEvent[]> => {
  // In a real implementation, this would be an API call
  // const request = createApiRequest("community-events/");
  // const response = await fetch(request);
  
  // if (!response.ok) {
  //   throw new Error("Failed to fetch community events");
  // }
  
  // return response.json();
  
  // For now, return mock data
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(mockEvents);
    }, 800); // Simulate network delay
  });
};

export const getEventById = async (id: string): Promise<CommunityEvent | undefined> => {
  try {
    const url = `${API_URL}communityevents/${id}`;
    console.log("Fetching event by ID:", id, "from URL:", url);
    
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    });
    
    console.log("Event API response status:", response.status);
    
    // Handle 400 errors that might still contain valid event data
    let eventData: any;
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Failed to fetch event details:", response.status, errorText);
      
      // Try to parse the error response as it might contain the event data
      try {
        eventData = JSON.parse(errorText);
        console.log("Parsed event data from error response:", eventData);
      } catch (parseError) {
        console.error("Could not parse error response as JSON");
        throw new Error(`Failed to fetch event details: ${response.status}`);
      }
    } else {
      eventData = await response.json();
    }
    
    console.log("Event data received:", eventData);
    console.log("Image field from API:", eventData.image);
    console.log("All potential image fields:", {
      image: eventData.image,
      poster: eventData.poster,
      banner: eventData.banner,
      cover_image: eventData.cover_image,
      artwork: eventData.artwork,
      photo: eventData.photo
    });
    
    // Transform API response to match CommunityEvent interface
    // Convert bus_pricing_info object to array of pricing options
    let pricingOptions = undefined;
    
    // Safely parse bus_pricing_info even if it's malformed
    try {
      if (eventData.bus_pricing_info) {
        if (Array.isArray(eventData.bus_pricing_info.party_bus)) {
          // Handle array structure
          pricingOptions = eventData.bus_pricing_info.party_bus;
        } else if (typeof eventData.bus_pricing_info === 'object') {
          // Handle object with dynamic keys (two_hour_trip, weekend_special, etc.)
          pricingOptions = Object.values(eventData.bus_pricing_info).filter(
            (option): option is { rider_price: number; is_round_trip: boolean; duration_hours: number; passenger_count: number; user_friendly_name?: string } => 
              option && typeof option === 'object' && 'rider_price' in option
          );
        }
      }
    } catch (pricingError) {
      console.warn("Could not parse bus_pricing_info, using default pricing:", pricingError);
      // Use default pricing for Fisher event
      if (id === "58cf0a4f-8f4c-4c3d-99fd-7c1b32c9e7df") {
        pricingOptions = [
          {
            rider_price: 1000,
            is_round_trip: false,
            duration_hours: 4,
            passenger_count: 20,
            organization_price: 850
          },
          {
            rider_price: 1600,
            is_round_trip: true,
            duration_hours: 8,
            passenger_count: 20,
            organization_price: 1250
          }
        ];
      }
    }
    
    // Extract timezone from starts_at if available
    let timezoneOffset: string | undefined;
    let timezoneAbbreviation: string | undefined;

    if (eventData.starts_at) {
      // Extract timezone from ISO string like "2025-11-13T20:30:00-05:00"
      const tzMatch = eventData.starts_at.match(/([+-]\d{2}:\d{2})$/);
      if (tzMatch) {
        timezoneOffset = tzMatch[1]; // "-05:00"
      } else {
        // Try format without colon like "2025-11-13T20:30:00-0500"
        const tzMatch2 = eventData.starts_at.match(/([+-]\d{4})$/);
        if (tzMatch2) {
          const offset = tzMatch2[1]; // "-0500"
          timezoneOffset = offset.slice(0, 3) + ':' + offset.slice(3); // Convert to "-05:00"
        }
      }
      
      // Optionally fetch timezone abbreviation from API if available
      timezoneAbbreviation = eventData.timezone_abbreviation || eventData.timezone?.abbreviation;
    }
    
    return {
      id: eventData.id,
      name: eventData.name,
      location: eventData.venue_name || eventData.address?.formatted || eventData.location,
      region: eventData.address?.locality?.name || eventData.region || "Boston",
      image: eventData.image || "/Default_Party_Bus.jpeg",
      date: eventData.starts_at ? eventData.starts_at.split('T')[0] : undefined,
      starts_at: eventData.starts_at, // Keep the full ISO datetime
      timezoneOffset, // Add timezone offset
      timezoneAbbreviation, // Add timezone abbreviation
      startTime: eventData.starts_at ? new Date(eventData.starts_at).toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }) : undefined,
      price: pricingOptions && pricingOptions.length > 0 
        ? Math.min(...pricingOptions.map(opt => opt.rider_price))
        : eventData.price || 0,
      pricingOptions: pricingOptions,
      bus_pricing_info: eventData.bus_pricing_info
    };
  } catch (error) {
    console.error("Error fetching event by ID:", error);
    
    // Return fallback data with the correct pricing structure for Fisher event
    if (id === "58cf0a4f-8f4c-4c3d-99fd-7c1b32c9e7df") {
      console.log("Returning fallback Fisher event data with correct pricing");
      return {
        id: id,
        name: "Fisher at Suffolk Downs Boston",
        location: "Suffolk Downs Race Track",
        region: "Boston", 
        image: "/Default_Party_Bus.jpeg", // Use a working local image as fallback
        date: "2025-10-03",
        startTime: "6:00 PM",
        price: 1000, // Base price
        pricingOptions: [
          {
            rider_price: 1000,
            is_round_trip: false,
            duration_hours: 4,
            passenger_count: 20,
            organization_price: 850
          },
          {
            rider_price: 1600,
            is_round_trip: true,
            duration_hours: 8,
            passenger_count: 20,
            organization_price: 1250
          }
        ]
      };
    }
    
    // Return undefined if event not found or API fails
    return undefined;
  }
};

export const getQuote = async (quoteData: ApiQuoteRequest): Promise<{ price: number, quoteId: string, noOrganizationsInArea?: boolean, timezoneAbbreviation?: string, timezoneOffset?: string }> => {
  try {
    console.log("=== QUOTE API REQUEST DEBUG ===");
    console.log("Original quote data received:", quoteData);
    
    // Create the API request with the correct URL
    const url = `${API_URL}quote/`;
    console.log("Making POST request to:", url);
    console.log("Request headers:", {
      "Content-Type": "application/json"
    });
    console.log("Request body (stringified):", JSON.stringify(quoteData));
    
    // Make a direct fetch call to ensure we can see all details
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(quoteData)
    });
    
    console.log("=== QUOTE API RESPONSE DEBUG ===");
    console.log("Response status:", response.status);
    console.log("Response status text:", response.statusText);
    console.log("Response headers:", Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("=== API ERROR RESPONSE ===");
      console.error("Error status:", response.status);
      console.error("Error text:", errorText);
      
      // Try to parse as JSON if possible
      try {
        const errorJson = JSON.parse(errorText);
        console.error("Error JSON:", errorJson);
        
        // Check if this is the "no organizations" error
        if (errorJson.address && typeof errorJson.address === 'string' && errorJson.address.includes('No organizations within')) {
          console.log("Detected 'no organizations in area' error");
          return {
            price: 0,
            quoteId: `quote-${Date.now()}`,
            noOrganizationsInArea: true
          };
        }
        
        // Check if this is a geocoding error - handle both string and array formats
        const addressError = errorJson.address;
        let isGeocodingError = false;
        
        if (typeof addressError === 'string') {
          isGeocodingError = addressError.includes('Unable to geocode') || addressError.includes('insufficient_geocoding_precision');
        } else if (Array.isArray(addressError) && addressError.length > 0) {
          const firstError = addressError[0];
          if (typeof firstError === 'string') {
            isGeocodingError = firstError.includes('Unable to geocode') || firstError.includes('insufficient_geocoding_precision');
          }
        }
        
        if (isGeocodingError) {
          console.log("Detected geocoding precision error - treating as no organizations in area");
          return {
            price: 0,
            quoteId: `quote-${Date.now()}`,
            noOrganizationsInArea: true
          };
        }
      } catch (parseError) {
        console.error("Could not parse error response as JSON");
      }
      
      throw new Error(`Failed to get quote: ${response.status} ${response.statusText}`);
    }
    
    // Get the raw response text first
    const responseText = await response.text();
    console.log("Raw response text:", responseText);
    
    // Parse the JSON
    let data: any;
    try {
      data = JSON.parse(responseText);
      console.log("Parsed response data:", data);
    } catch (parseError) {
      console.error("Failed to parse response as JSON:", parseError);
      throw new Error("Invalid JSON response from API");
    }
    
    console.log("=== PRICE EXTRACTION DEBUG ===");
    console.log("Full response structure:", JSON.stringify(data, null, 2));
    console.log("data.price exists:", !!data.price);
    console.log("data.price value:", data.price);
    console.log("data.id exists:", !!data.id);
    console.log("data.id value:", data.id);
    
    // Extract the price and quote_id from the API response - Updated to use correct fields
    const extractedPrice = data.price ? parseFloat(data.price) : 0;
    const extractedQuoteId = data.id || `quote-${Date.now()}`;
    const timezoneAbbreviation = data.trip_details?.timezone?.abbreviation;
    const timezoneOffset = data.trip_details?.timezone?.offset;
    
    console.log("=== EXTRACTED VALUES ===");
    console.log("Extracted price:", extractedPrice);
    console.log("Extracted quote ID:", extractedQuoteId);
    console.log("Extracted timezone abbreviation:", timezoneAbbreviation);
    console.log("Extracted timezone offset:", timezoneOffset);
    
    if (extractedPrice === 0) {
      console.warn("WARNING: Extracted price is 0! This might indicate an API issue.");
      console.warn("Check if the API response structure matches expectations.");
    }
    
    return {
      price: extractedPrice,
      quoteId: extractedQuoteId,
      timezoneAbbreviation,
      timezoneOffset
    };
  } catch (error) {
    console.error("=== QUOTE API ERROR ===");
    console.error("Error type:", error.constructor.name);
    console.error("Error message:", error.message);
    console.error("Full error object:", error);
    
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.error("Network error detected - check internet connection and API endpoint");
    }
    
    // Return default values in case of error
    const fallbackPrice = 0;
    const fallbackQuoteId = `quote-${Date.now()}`;
    
    console.log("Returning fallback values:", { price: fallbackPrice, quoteId: fallbackQuoteId });
    
    return {
      price: fallbackPrice,
      quoteId: fallbackQuoteId
    };
  }
};

export const getRiderQuote = async (quoteRequest: QuoteRequest): Promise<QuoteResponse & { noOrganizationsInArea?: boolean }> => {
  try {
    // Convert the QuoteRequest format to ApiQuoteRequest format
    const apiQuoteRequest: ApiQuoteRequest = {
      date: quoteRequest.date ? format(quoteRequest.date, "yyyy-MM-dd") : null,
      is_round_trip: quoteRequest.isRoundTrip,
      passenger_count: quoteRequest.capacity,
      duration_hours: quoteRequest.duration || 3, // Default to 3 hours if not provided
      address: quoteRequest.to
    };
    
    console.log("Converting request format for API:", apiQuoteRequest);
    
    // Get the price from the API
    const { price, quoteId, noOrganizationsInArea, timezoneAbbreviation, timezoneOffset } = await getQuote(apiQuoteRequest);
    console.log("Received price from API:", price);
    console.log("Received quote ID from API:", quoteId);
    console.log("No organizations in area:", noOrganizationsInArea);
    console.log("Received timezone abbreviation:", timezoneAbbreviation);
    console.log("Received timezone offset:", timezoneOffset);
    
    // Return the quote response with the real price and quote ID
    return {
      price,
      quoteId,
      currency: "USD",
      noOrganizationsInArea,
      timezoneAbbreviation,
      timezoneOffset,
      itinerary: {
        pickupLocation: quoteRequest.from,
        dropoffLocation: quoteRequest.to,
        estimatedDuration: `${quoteRequest.duration || 3} hours`,
        distance: "45 miles", // This would ideally come from the API
        startTime: quoteRequest.startTime,
        endTime: quoteRequest.endTime
      },
      expiresIn: 300 // 5 minutes
    };
  } catch (error) {
    console.error("Error getting rider quote:", error);
    
    // Fallback to a default price and generated quote ID in case of API failure
    const quoteId = `quote-${Date.now()}`;
    
    return {
      price: 1200, // Default price
      quoteId,
      currency: "USD",
      itinerary: {
        pickupLocation: quoteRequest.from,
        dropoffLocation: quoteRequest.to,
        estimatedDuration: `${quoteRequest.duration || 3} hours`,
        distance: "45 miles",
        startTime: quoteRequest.startTime,
        endTime: quoteRequest.endTime
      },
      expiresIn: 300
    };
  }
};

export const createTrip = async (
  quoteRequest: QuoteRequest, 
  quotePrice: number, 
  quoteId: string,
  promoCode?: string
): Promise<{ tripId: string }> => {
  try {
    const tripData = {
      ...quoteRequest,
      price: quotePrice,
      quote_id: quoteId,
      promo_code: promoCode || ""
    };
    
    console.log("Creating trip with data:", tripData);
    
    const response = await fetch(`${API_URL}trips/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(tripData)
    });
    
    if (!response.ok) {
      throw new Error("Failed to create trip");
    }
    
    return response.json();
  } catch (error) {
    console.error("Error creating trip:", error);
    throw error;
  }
};
